import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { Filter } from '@/components/DataTable/FilterControls'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  exportMultipliers,
  useMultipliersList,
  useUpdateMultipliers,
} from '@/hooks/queries/credit/useMultipliers'
import { MultipleToggleListControl } from '@/lib/utils/filters'
import { UpdateMultipliers } from '@repo/backend/credit'
import {
  getRiskClassName,
  RCL_MULTIPLIER_KEYS,
  RCL_MULTIPLIER_RISK_CLASS_CODES,
  rclMultiplierKey,
} from '@repo/constants'
import { hasPermissions } from '@repo/permissions'
import { createFileRoute } from '@tanstack/react-router'
import { RowSelectionState } from '@tanstack/react-table'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MultiplierBulkEditBar } from './-components/MultiplierBulkEditBar'
import MultiplierDataTable, { MultiplierRow } from './-components/MultiplierDataTable'
import { UploadMultipliersDataDialog } from './-components/UploadMultipliersDataDialog'

export const Route = createFileRoute('/_auth/_modules/credit/operations/multiplier/')({
  component: RouteComponent,
})

type EditedValue = {
  multiplier?: string
}

// The multiplier column is numeric(10,4), so persisted values come back with trailing zeros
// ("1.8500"), while NumberCellInput commits the trimmed form ("1.85"). Normalize both sides to the
// same shortest numeric form before comparing so re-committing an unchanged value isn't flagged as
// an edit. Empty/non-numeric strings are left as-is.
const normalizeMultiplier = (value: string) =>
  value === '' || isNaN(Number(value)) ? value : String(Number(value))

function RouteComponent() {
  const { t } = useTranslation('credit')
  const { permissions } = usePermissions()
  const canEdit = hasPermissions(permissions, 'credit_multiplier_edit')

  // Edits are keyed by the natural key (payment term × risk class × PAD), not a database id, so any
  // cell can be edited whether or not it has a persisted row yet - saving upserts.
  const [editedValues, setEditedValues] = useState<Map<string, EditedValue>>(new Map())
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  // `selected` is the pending choice in the filter panel; `applied` is what actually filters the
  // grid - committed only when the user clicks "Show results".
  const [selectedPaymentTerms, setSelectedPaymentTerms] = useState<string[]>([])
  const [appliedPaymentTerms, setAppliedPaymentTerms] = useState<string[]>([])
  const [selectedRiskClasses, setSelectedRiskClasses] = useState<string[]>([])
  const [appliedRiskClasses, setAppliedRiskClasses] = useState<string[]>([])

  const { data, isPending, error } = useMultipliersList()

  const { mutate: updateMultipliers, isPending: isSaving } = useUpdateMultipliers({
    handleOnSuccess: () => {
      setEditedValues(new Map())
    },
  })

  // The set of keys is app-defined: every payment term × risk class × PAD permutation. We render the
  // full grid and overlay the persisted multiplier where the database has one; keys with no stored
  // value stay empty for the user to fill in.
  const persistedByKey = new Map((data?.results ?? []).map((row) => [rclMultiplierKey(row), row]))
  const results: MultiplierRow[] = RCL_MULTIPLIER_KEYS.map((key) => ({
    paymentTerm: key.paymentTerm,
    riskClassCode: key.riskClassCode,
    pad: key.pad,
    multiplier: persistedByKey.get(rclMultiplierKey(key))?.multiplier ?? null,
  }))

  const originalByKey = new Map(results.map((row) => [rclMultiplierKey(row), row]))

  const paymentTermOptions = Array.from(new Set(results.map((row) => row.paymentTerm)))

  const filteredData = results.filter(
    (row) =>
      (appliedPaymentTerms.length === 0 || appliedPaymentTerms.includes(row.paymentTerm)) &&
      (appliedRiskClasses.length === 0 || appliedRiskClasses.includes(row.riskClassCode)),
  )

  const paymentTermFilter: Filter<string[]> = {
    control: MultipleToggleListControl,
    name: t('multiplier.filter.paymentTerm'),
    title: t('multiplier.filter.paymentTerm'),
    options: paymentTermOptions.map((value) => ({ value, label: value })),
    value: selectedPaymentTerms,
    onChange: setSelectedPaymentTerms,
  }

  const riskClassFilter: Filter<string[]> = {
    control: MultipleToggleListControl,
    name: t('multiplier.filter.riskClass'),
    title: t('multiplier.filter.riskClass'),
    options: RCL_MULTIPLIER_RISK_CLASS_CODES.map((code) => ({
      value: code,
      label: t(`riskClass.${getRiskClassName(code)}`),
    })),
    value: selectedRiskClasses,
    onChange: setSelectedRiskClasses,
  }

  const appliedFilters = [
    ...(appliedPaymentTerms.length > 0
      ? [{ name: t('multiplier.filter.paymentTerm'), value: appliedPaymentTerms }]
      : []),
    ...(appliedRiskClasses.length > 0
      ? [
          {
            name: t('multiplier.filter.riskClass'),
            value: appliedRiskClasses.map((code) => t(`riskClass.${getRiskClassName(code)}`)),
          },
        ]
      : []),
  ]

  const handleValueChange = useCallback(
    (key: string, field: keyof EditedValue, value: string) => {
      // Clamp negatives to 0, but keep an empty field empty (unset multiplier).
      const sanitized = value && Number(value) < 0 ? '0' : value

      setEditedValues((prev) => {
        const newMap = new Map(prev)
        const existing = newMap.get(key) ?? {}
        const updated = { ...existing, [field]: sanitized }

        const original = originalByKey.get(key)
        if (original) {
          const allMatch = Object.entries(updated).every(
            ([field, value]) =>
              normalizeMultiplier(value) ===
              normalizeMultiplier(original[field as keyof EditedValue] ?? ''),
          )
          if (allMatch) {
            newMap.delete(key)
            return newMap
          }
        }

        newMap.set(key, updated)
        return newMap
      })
    },
    [originalByKey],
  )

  const getDisplayValue = useCallback(
    (row: MultiplierRow, field: keyof EditedValue): string => {
      const edited = editedValues.get(rclMultiplierKey(row))
      if (edited && edited[field] !== undefined) {
        return edited[field] ?? ''
      }
      return row[field] ?? ''
    },
    [editedValues],
  )

  const handleSaveChanges = () => {
    if (editedValues.size === 0) return

    const updates: UpdateMultipliers['updates'] = []

    editedValues.forEach((values, key) => {
      if (values.multiplier === undefined) return
      const row = originalByKey.get(key)
      if (!row) return
      updates.push({
        paymentTerm: row.paymentTerm,
        riskClassCode: row.riskClassCode,
        pad: row.pad,
        multiplier: values.multiplier,
      })
    })

    updateMultipliers({ updates })
  }

  // Selection keys are the row's index into the data passed to the table (filteredData).
  const selectedRows = filteredData.filter((_, index) => rowSelection[index])
  const areAllSelected = filteredData.length > 0 && selectedRows.length === filteredData.length

  // Select every filtered row across all pages (the header checkbox only covers the current page).
  const toggleSelectAll = () => {
    if (areAllSelected) {
      setRowSelection({})
      return
    }
    setRowSelection(Object.fromEntries(filteredData.map((_, index) => [index.toString(), true])))
  }

  const handleBulkEdit = (multiplier: string) => {
    const sanitized = multiplier && Number(multiplier) < 0 ? '0' : multiplier

    setEditedValues((prev) => {
      const newMap = new Map(prev)
      selectedRows.forEach((row) => {
        const key = rclMultiplierKey(row)
        const original = originalByKey.get(key)
        if (
          original &&
          normalizeMultiplier(original.multiplier ?? '') === normalizeMultiplier(sanitized)
        ) {
          newMap.delete(key)
        } else {
          newMap.set(key, { multiplier: sanitized })
        }
      })
      return newMap
    })

    setRowSelection({})
  }

  const areChangesPending = editedValues.size > 0

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <Spread className="items-center">
        <PageHeader>{t('multiplier.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            label={t('multiplier.export')}
            getCsvExport={() => exportMultipliers()}
            rowCount={results.length}
          />
          <PermissionsGuard required={['credit_multiplier_edit']}>
            <UploadMultipliersDataDialog />
          </PermissionsGuard>
        </HStack>
      </Spread>

      <MultiplierDataTable
        data={filteredData}
        isPending={isPending}
        error={error}
        editedValues={editedValues}
        filters={[paymentTermFilter, riskClassFilter] as Filter<unknown>[]}
        appliedFilters={appliedFilters}
        onShowResults={() => {
          setAppliedPaymentTerms(selectedPaymentTerms)
          setAppliedRiskClasses(selectedRiskClasses)
          setRowSelection({})
        }}
        onResetAllFilters={() => {
          setSelectedPaymentTerms([])
          setAppliedPaymentTerms([])
          setSelectedRiskClasses([])
          setAppliedRiskClasses([])
          setRowSelection({})
        }}
        canSelect={canEdit}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        contentSlot={
          canEdit && selectedRows.length > 0 ? (
            <MultiplierBulkEditBar
              selectedCount={selectedRows.length}
              totalCount={filteredData.length}
              areAllSelected={areAllSelected}
              onApply={handleBulkEdit}
              onToggleSelectAll={toggleSelectAll}
            />
          ) : undefined
        }
        getDisplayValue={getDisplayValue}
        onValueChange={handleValueChange}
      />

      <PermissionsGuard required={['credit_multiplier_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
